import React, { useState, useEffect, useCallback } from "react";
import API_BASE from "./apiConfig";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import { useDateTimeFormat } from "./utils/formatters";
import { estimateProration } from "./utils/proration";
import StatusBadge from "./components/StatusBadge";
import {
  FaClipboardList, FaCheckCircle, FaTimesCircle, FaClock,
  FaUser, FaUserFriends, FaGraduationCap, FaFileSignature,
  FaSearch, FaEye, FaCheck, FaTimes, FaDownload, FaSave,
  FaExclamationCircle, FaMapMarkerAlt, FaCreditCard, FaReceipt,
  FaCalendarAlt, FaDollarSign, FaStickyNote, FaKey,
  FaPlus, FaTrash, FaTag, FaPercent, FaShieldAlt, FaLock, FaIdCard,
} from "react-icons/fa";

const BASE      = API_BASE;
const DEFAULT_ADMIN_FEE = 25.00;

const FALLBACK_ROLES = {
  "Batting":   ["Opening Batter","Middle Order Batter","Lower Order Batter"],
  "Bowling":   ["Fast Bowler","Medium Pacer","Spinner"],
  "All-Round": ["All-Rounder","Batting All-Rounder","Bowling All-Rounder"],
  "Keeping":   ["Wicket Keeper","Wicket Keeper Batter"],
};

const STATUS_META = {
  pending:  { label:"Pending Review", color:getBrandColors().orange, bg:"rgba(217,119,6,0.08)",  icon:<FaClock size={11}/> },
  approved: { label:"Approved",       color:getBrandColors().green, bg:"rgba(5,150,105,0.08)",  icon:<FaCheckCircle size={11}/> },
  rejected: { label:"Rejected",       color:getBrandColors().red, bg:"rgba(220,38,38,0.08)",  icon:<FaTimesCircle size={11}/> },
};

const TABS = ["pending","approved","rejected"];
const MONTH_NAMES  = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const CYCLE_LABEL  = { monthly:"Monthly", quarterly:"Quarterly", semi_annual:"Semi-Annual", annual:"Annual" };
const CYCLE_SHORT  = { monthly:"mo", quarterly:"qtr", semi_annual:"6mo", annual:"yr" };
// A "recurring" extra fee is always entered as its MONTHLY amount (matches
// how admins describe it, e.g. "$20/month tournament fee") — but the actual
// recurring schedule bills once per `cycle`, not once per month. Without
// scaling by the number of months in the cycle, a quarterly student's
// schedule silently dropped 2 of every 3 months' worth of the fee.
const CYCLE_MONTHS = { monthly:1, quarterly:3, semi_annual:6, annual:12 };
const FEE_KEY      = { monthly:"fee_monthly", quarterly:"fee_quarterly", semi_annual:"fee_semi_annual", annual:"fee_annual" };
const DT_KEY       = { monthly:"disc_monthly_type", quarterly:"disc_quarterly_type", semi_annual:"disc_semi_type", annual:"disc_annual_type" };
const DV_KEY       = { monthly:"disc_monthly_value", quarterly:"disc_quarterly_value", semi_annual:"disc_semi_value", annual:"disc_annual_value" };

function StatusChip({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const tone = status==="pending" ? "warning" : (status==="approved"||status==="active") ? "success" : "danger";
  return <StatusBadge tone={tone} icon={m.icon} label={m.label} />;
}

/* ── Base fee extractor from program object ─────────────────────────────────── */
function getProgramPricing(prog, cycle) {
  if (!prog) return { gross:0, discAmt:0, discLabel:null, net:0 };
  const gross  = parseFloat(prog[FEE_KEY[cycle]]||0);
  const dtype  = (prog[DT_KEY[cycle]]||"").toLowerCase();
  const dval   = parseFloat(prog[DV_KEY[cycle]]||0);
  let discAmt = 0, discLabel = null;
  if (dtype==="pct"&&dval>0) { discAmt=Math.round(gross*dval/100*100)/100; discLabel=`${dval}%`; }
  else if (dtype==="amt"&&dval>0) { discAmt=dval; discLabel=`$${dval.toFixed(2)}`; }
  return { gross, discAmt, discLabel, net:Math.max(0,gross-discAmt) };
}

function getMonthlyNet(prog) {
  if (!prog) return 0;
  const { net } = getProgramPricing(prog, "monthly");
  return net;
}

/* ── Full billing calculator (includes admin overrides, extras, discount) ──── */
function calcTotal({ prog, cycle, adminFee, extraFees, specialDisc }) {
  const today       = new Date();
  const curMonth    = MONTH_NAMES[today.getMonth()];
  const nextD       = new Date(today.getFullYear(), today.getMonth()+1, 1);
  const nextCycle   = `${MONTH_NAMES[nextD.getMonth()]} ${nextD.getFullYear()}`;
  const monthlyNet  = getMonthlyNet(prog);
  const { daysInMonth, daysLeft, totalSessions, remainingSessions, prorated } =
    estimateProration(prog?.days_per_week, monthlyNet, today);
  const { gross, discAmt, discLabel, net } = getProgramPricing(prog, cycle);

  const adminFeeAmt   = parseFloat(adminFee||0);
  const extraOneTime  = extraFees.filter(f=>!f.recurring).reduce((s,f)=>s+parseFloat(f.amount||0),0);
  // Recurring extras are entered as a monthly amount — scale by the number
  // of months in the billing cycle so a quarterly/semi-annual/annual charge
  // actually includes every month's worth of the fee, not just one.
  const cycleMonths   = CYCLE_MONTHS[cycle] || 1;
  const extraRecurring= extraFees.filter(f=>f.recurring).reduce((s,f)=>s+parseFloat(f.amount||0),0) * cycleMonths;

  const rawDiscVal    = parseFloat(specialDisc?.amount||0);
  const discIsRecur   = specialDisc?.recurring||false;
  // Resolve percent discount against prorated (first payment) or net (recurring)
  const discAmt2      = specialDisc?.discType === "pct"
    ? (discIsRecur
        ? Math.round(net * rawDiscVal / 100 * 100) / 100
        : Math.round(prorated * rawDiscVal / 100 * 100) / 100)
    : rawDiscVal;
  const firstDisc     = discIsRecur ? 0 : discAmt2;
  const recurDisc     = discIsRecur ? (specialDisc?.discType === "pct"
    ? Math.round(net * rawDiscVal / 100 * 100) / 100 : discAmt2) : 0;

  const totalNow      = Math.max(0, prorated + adminFeeAmt + extraOneTime - firstDisc);
  const recurringNet  = Math.max(0, net + extraRecurring - recurDisc);

  return {
    prorated, monthlyNet, daysLeft, daysInMonth, totalSessions, remainingSessions, curMonth, nextCycle,
    gross, discAmt, discLabel, net,
    adminFeeAmt, extraOneTime, extraRecurring,
    firstDisc, recurDisc, totalNow, recurringNet,
  };
}

/* ── Detail Panel ─────────────────────────────────────────────────────────── */
export function DetailPanel({ sub, currentUser, programsList, onClose, onRefresh }) {
  const { isDark } = useTheme();
  const T          = getT(isDark);
  const { fmtDate } = useDateTimeFormat();

  // Once a submission has been approved or rejected, the record is final —
  // no further field edits or re-approval/re-rejection should be possible.
  const isLocked = sub.status !== "pending";

  const [form, setForm]                 = useState({ ...sub });
  const [saving, setSaving]             = useState(false);
  const [showPayModal, setPayModal]     = useState(false);
  const [paying, setPaying]             = useState(false);
  const [showReject, setShowReject]     = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [msg, setMsg]                   = useState(null);
  const [approveResult, setApproveResult] = useState(null);

  // Inline card capture in approval panel
  const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
  const [showCardCapture, setShowCardCapture] = useState(false);
  const [cardSaving,      setCardSaving]      = useState(false);
  const [cardMsg,         setCardMsg]         = useState(null);
  const approvalStripeRef = React.useRef(null);
  const approvalCardElRef = React.useRef(null);
  const approvalMountedRef = React.useRef(false);

  // Billing overrides
  const [adminFee,    setAdminFee]    = useState(DEFAULT_ADMIN_FEE);
  const [extraFees,   setExtraFees]   = useState([]); // [{id,label,amount,recurring}]
  const [specialDisc, setSpecialDisc] = useState({ amount:"", discType:"amt", label:"", recurring:false });
  const [newFeeLabel, setNewFeeLabel] = useState("");
  const [newFeeAmt,   setNewFeeAmt]   = useState("");
  const [newFeeRecur, setNewFeeRecur] = useState(false);

  // Year filter for program dropdown
  const [progYear, setProgYear] = useState("");

  // Resolve program object from list
  const program = programsList.find(p => String(p.program_id) === String(form.program_id)) || null;

  // Skill Level / Playing Role dropdown options — this screen used to show
  // these as free-text inputs with no options at all (a regression against
  // the public RegistrationForm.js, which correctly uses dropdowns), so any
  // value an admin corrected here had to be typed by hand with no validation
  // against what the rest of the system actually recognizes.
  const skillLevelOptions = [...new Set(programsList.map(p => p.level_name).filter(Boolean))];
  const [rolesByGroup, setRolesByGroup] = useState(FALLBACK_ROLES);
  useEffect(() => {
    fetch(`${BASE}/students/playing-roles`).then(r => r.json()).then(d => {
      if (Array.isArray(d) && d.length) {
        const g = d.reduce((acc, r) => {
          const grp = r.role_group || "Other";
          (acc[grp] = acc[grp] || []).push(r.role_name || r);
          return acc;
        }, {});
        setRolesByGroup(g);
      }
    }).catch(() => {});
  }, []);

  // Distinct years from programs (via valid_from year)
  const progYears = [...new Set(
    programsList
      .map(p => p.valid_from ? String(p.valid_from).slice(0,4) : null)
      .filter(Boolean)
  )].sort();
  const multiYear = progYears.length > 1;
  const filteredPrograms = (multiYear && progYear)
    ? programsList.filter(p => p.valid_from && String(p.valid_from).slice(0,4) === progYear)
    : programsList;

  useEffect(() => {
    setForm({ ...sub }); setMsg(null); setShowReject(false);
    setPayModal(false); setApproveResult(null);
    // Restore whatever was last saved via "Save Changes" — previously this
    // always reset to blank defaults, so even though nothing actually
    // persisted these fields, the fact that this ran again after every
    // save (Save Changes calls onRefresh(), which re-fetches `sub` and
    // re-triggers this effect) meant they got wiped off the SCREEN too,
    // making "Save Changes" look like it silently discarded them.
    setAdminFee(sub.admin_fee != null ? sub.admin_fee : DEFAULT_ADMIN_FEE);
    try { setExtraFees(sub.extra_fees_json ? JSON.parse(sub.extra_fees_json) : []); }
    catch { setExtraFees([]); }
    try {
      setSpecialDisc(sub.special_discount_json
        ? JSON.parse(sub.special_discount_json)
        : { amount:"", discType:"amt", label:"", recurring:false });
    } catch { setSpecialDisc({ amount:"", discType:"amt", label:"", recurring:false }); }
    setProgYear("");
    setNewFeeLabel(""); setNewFeeAmt(""); setNewFeeRecur(false);
    // Reset card capture when switching submissions
    setShowCardCapture(false); setCardMsg(null);
    approvalMountedRef.current = false; approvalCardElRef.current = null;
  }, [sub]);

  // Mount Stripe card element when admin opens the inline capture form
  useEffect(() => {
    if (!showCardCapture || !STRIPE_PK || approvalMountedRef.current) return;
    let cancelled = false;
    import("@stripe/stripe-js").then(({ loadStripe }) => loadStripe(STRIPE_PK)).then(stripe => {
      if (cancelled || !stripe) return;
      approvalStripeRef.current = stripe;
      const elements = stripe.elements();
      const card = elements.create("card", {
        style: { base: { fontSize: "14px", color: isDark ? "#e2e8f0" : "#1a2840",
          "::placeholder": { color: isDark ? "rgba(255,255,255,0.35)" : "rgba(3,78,123,0.40)" } } },
        hidePostalCode: true,
      });
      const el = document.getElementById("approval-card-el");
      if (el) { card.mount(el); approvalCardElRef.current = card; approvalMountedRef.current = true; }
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [showCardCapture, isDark]);

  const handleSaveCard = async () => {
    if (!approvalStripeRef.current || !approvalCardElRef.current) return;
    setCardSaving(true); setCardMsg(null);
    try {
      const { paymentMethod, error } = await approvalStripeRef.current.createPaymentMethod({
        type: "card",
        card: approvalCardElRef.current,
        billing_details: {
          name:  form.parent_name  || "",
          email: form.parent_email || "",
          phone: form.parent_phone || "",
        },
      });
      if (error) { setCardMsg({ ok: false, text: error.message }); setCardSaving(false); return; }

      // Save PM to backend — stores in registration_submissions + creates/updates parent_accounts
      const res = await fetch(`${BASE}/registration/${sub.submission_id}/save-card`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stripe_pm_id:          paymentMethod.id,
          stripe_card_brand:     paymentMethod.card?.brand     || null,
          stripe_card_last4:     paymentMethod.card?.last4     || null,
          stripe_card_exp_month: paymentMethod.card?.exp_month || null,
          stripe_card_exp_year:  paymentMethod.card?.exp_year  || null,
        }),
      });
      const d = await res.json();
      if (d.success) {
        setCardMsg({ ok: true, text: `Card saved — ${(paymentMethod.card?.brand||"card").toUpperCase()} •••• ${paymentMethod.card?.last4}` });
        setShowCardCapture(false);
        // Refresh the panel so card block updates
        onRefresh();
      } else {
        setCardMsg({ ok: false, text: d.message || "Failed to save card." });
      }
    } catch (e) {
      setCardMsg({ ok: false, text: "Network error saving card." });
    }
    setCardSaving(false);
  };

  const billing = calcTotal({
    prog: program, cycle: form.billing_cycle||"quarterly",
    adminFee, extraFees, specialDisc,
  });

  /* ── Styles ── */
  const inp = {
    width:"100%", boxSizing:"border-box", padding:"7px 10px",
    borderRadius:7, fontSize:13, outline:"none",
    border:`1.5px solid ${isDark?"rgba(255,255,255,0.15)":"#d1d5db"}`,
    background: isDark?"rgba(255,255,255,0.07)":"#fff",
    color: isDark?"#f1f5f9":"#1e293b", colorScheme:isDark?"dark":"light",
  };
  const selInp = { ...inp, background: isDark?"rgba(4,14,32,0.95)":"#fff" };
  const numInp = { ...inp, width:"auto", textAlign:"right", minWidth:80 };
  const lbl = (t, req) => (
    <div style={{ fontSize:10, fontWeight:700, color:isDark?"#94a3b8":"#64748b",
                  textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:3 }}>
      {t}{req&&<span style={{color:"#ef4444",marginLeft:2}}>*</span>}
    </div>
  );
  const card = (accent) => ({
    background: isDark?"rgba(255,255,255,0.04)":"#fff",
    border: `1px solid ${isDark?"rgba(255,255,255,0.1)":"#e2e8f0"}`,
    borderLeft: `3px solid ${accent||getBrandColors().blue}`,
    borderRadius:10, padding:"12px 14px", marginBottom:10,
  });
  const secHead = (icon, title, sub2) => (
    <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:10 }}>
      <div style={{ color:getBrandColors().blue, opacity:0.7 }}>{icon}</div>
      <div>
        <div style={{ fontSize:11, fontWeight:800, color:isDark?"#e2e8f0":getBrandColors().blue,
                      textTransform:"uppercase", letterSpacing:"0.06em" }}>{title}</div>
        {sub2&&<div style={{fontSize:10,color:"#94a3b8",marginTop:1}}>{sub2}</div>}
      </div>
    </div>
  );
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  /* ── Program change helper ── */
  const selectProgram = (progId) => {
    const p = programsList.find(x=>String(x.program_id)===String(progId));
    setF("program_id", progId);
    if (p) setF("program_name", p.program_name);
  };

  /* ── Extra fee helpers ── */
  const addFee = () => {
    if (!newFeeLabel.trim() || !newFeeAmt) return;
    setExtraFees(f=>[...f,{id:Date.now(),label:newFeeLabel.trim(),amount:parseFloat(newFeeAmt),recurring:newFeeRecur}]);
    setNewFeeLabel(""); setNewFeeAmt(""); setNewFeeRecur(false);
  };
  const removeFee = id => setExtraFees(f=>f.filter(x=>x.id!==id));

  /* ── Save ── */
  const handleSave = async () => {
    setSaving(true); setMsg(null);
    const res  = await fetch(`${BASE}/registration/${sub.submission_id}`, {
      method:"PUT", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ ...form, admin_fee: adminFee, extra_fees: extraFees, special_discount: specialDisc }),
    });
    const data = await res.json();
    setSaving(false);
    setMsg({ ok:data.success, text:data.success?"Changes saved.":(data.message||"Save failed.") });
    if (data.success) onRefresh();
  };

  /* ── Pay & Approve ── */
  const handlePayApprove = async () => {
    setPaying(true); setMsg(null);
    // approve_submission builds the student record from whatever is
    // currently in registration_submissions in the DB — it never reads
    // profile-field edits from this request body. Without persisting `form`
    // first, any correction typed on this screen (address, DOB, skill
    // level, etc.) that admin didn't separately click "Save Changes" for
    // is silently discarded the moment the student record is created.
    try {
      await fetch(`${BASE}/registration/${sub.submission_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, admin_fee: adminFee, extra_fees: extraFees, special_discount: specialDisc }),
      });
    } catch { /* non-fatal — approve still proceeds against last-saved data */ }
    const res = await fetch(`${BASE}/registration/${sub.submission_id}/approve`, {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({
        reviewed_by:       currentUser?.username || "admin",
        notes:             form.notes,
        prorated_amount:   billing.prorated,
        admin_fee:         billing.adminFeeAmt,
        total_paid:        billing.totalNow,
        recurring_amount:  billing.recurringNet,
        prorate_days:      billing.daysLeft,
        prorate_days_total: billing.daysInMonth,
        extra_fees:        extraFees,
        special_discount:  specialDisc,
      }),
    });
    const data = await res.json();
    setPaying(false);
    if (data.success) {
      setApproveResult(data);
      setMsg({ ok:true, text:`✓ Student #${data.student_id} activated — portal created.` });
      setPayModal(false);
      onRefresh();
      // Return to queue after brief confirmation display
      setTimeout(() => onClose(), 2500);
    } else {
      setMsg({ ok:false, text:data.message||"Approval failed. Please check and retry." });
      setPayModal(false);
    }
  };

  /* ── Reject ── */
  const handleReject = async () => {
    if (!rejectReason.trim()) { setMsg({ok:false,text:"Enter a rejection reason first."}); return; }
    const res = await fetch(`${BASE}/registration/${sub.submission_id}/reject`, {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({
        reviewed_by: currentUser?.username||"admin",
        rejection_reason: rejectReason, notes: form.notes,
      }),
    });
    const data = await res.json();
    if (data.success) {
      setMsg({ok:true,text:"Submission rejected."});
      setShowReject(false);
      onRefresh();
      setTimeout(() => onClose(), 2500);
    } else {
      setMsg({ok:false,text:data.message||"Reject failed."});
    }
  };

  /* ── Billing row helper ── */
  const BRow = ({label, value, sub2, muted, bold, green, red, large, strike, indented}) => (
    <div style={{display:"grid",gridTemplateColumns:"1fr auto",alignItems:"baseline",
                 padding:"4px 0",borderBottom:`1px dashed ${isDark?"rgba(255,255,255,0.07)":"#f1f5f9"}`,
                 ...(indented?{paddingLeft:12}:{})}}>
      <div>
        <span style={{fontSize:muted?11:12,color:muted?"#94a3b8":isDark?"#cbd5e1":"#475569",fontStyle:muted?"italic":"normal"}}>{label}</span>
        {sub2&&<div style={{fontSize:10,color:"#94a3b8",lineHeight:1.3}}>{sub2}</div>}
      </div>
      <span style={{fontSize:large?17:bold?14:12,fontWeight:bold||large?800:500,
                    textDecoration:strike?"line-through":"none",
                    color:green?getBrandColors().green:red?"#ef4444":large||(bold&&!muted)?(isDark?getBrandColors().yellow:getBrandColors().blue):(isDark?"#e2e8f0":"#1e293b"),
                    textAlign:"right",whiteSpace:"nowrap"}}>{value}</span>
    </div>
  );

  /* ── Payment confirmation modal ── */
  const PayModal = () => (
    <div style={{position:"fixed",inset:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",
                 background:"rgba(0,0,0,0.55)",backdropFilter:"blur(4px)", WebkitBackdropFilter: "blur(4px)"}}>
      <div style={{background:isDark?"#0f172a":"#fff",border:`1px solid ${isDark?"rgba(255,255,255,0.15)":"#e2e8f0"}`,
                   borderRadius:14,padding:"24px 28px",width:460,maxWidth:"90vw",
                   boxShadow:"0 24px 60px rgba(0,0,0,0.4)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}>
          <div style={{width:36,height:36,borderRadius:9,background:"rgba(5,150,105,0.12)",
                       display:"flex",alignItems:"center",justifyContent:"center"}}>
            <FaCreditCard size={16} color={getBrandColors().green}/>
          </div>
          <div>
            <div style={{fontSize:15,fontWeight:800,color:isDark?"#f1f5f9":getBrandColors().blue}}>Confirm Payment &amp; Activate</div>
            <div style={{fontSize:11,color:"#94a3b8"}}>{form.first_name} {form.last_name} — {form.program_name}</div>
          </div>
        </div>

        <div style={{background:isDark?"rgba(255,255,255,0.04)":"#f8fafc",
                     border:`1px solid ${isDark?"rgba(255,255,255,0.1)":"#e2e8f0"}`,
                     borderRadius:9,padding:"12px 14px",marginBottom:14}}>
          {/* First payment breakdown */}
          <div style={{fontSize:10,fontWeight:800,color:"#f59e0b",textTransform:"uppercase",
                       letterSpacing:"0.05em",marginBottom:8}}>First Payment</div>
          <BRow label={`Prorated — ${billing.curMonth} (${billing.remainingSessions}/${billing.totalSessions} sessions @ $${billing.monthlyNet.toFixed(2)}/mo)`}
                value={`$${billing.prorated.toFixed(2)}`}/>
          <BRow label="Administrative Fee" value={`$${billing.adminFeeAmt.toFixed(2)}`}/>
          {extraFees.filter(f=>!f.recurring).map(f=>(
            <BRow key={f.id} label={f.label} value={`$${parseFloat(f.amount||0).toFixed(2)}`} indented/>
          ))}
          {billing.firstDisc>0 && (
            <BRow label={`Special Discount${specialDisc.label?` — ${specialDisc.label}`:""}`}
                  value={`-$${billing.firstDisc.toFixed(2)}`} red/>
          )}
          <div style={{display:"flex",justifyContent:"space-between",paddingTop:9,marginTop:4,
                       borderTop:`2px solid ${isDark?"rgba(255,255,255,0.12)":"#e2e8f0"}`}}>
            <span style={{fontSize:14,fontWeight:800,color:isDark?"#f1f5f9":getBrandColors().blue}}>Total Charged Now</span>
            <span style={{fontSize:20,fontWeight:900,color:isDark?getBrandColors().yellow:getBrandColors().blue}}>${billing.totalNow.toFixed(2)}</span>
          </div>
          {/* Recurring */}
          <div style={{marginTop:12,paddingTop:10,borderTop:`1px solid ${isDark?"rgba(255,255,255,0.07)":"#f1f5f9"}`}}>
            <div style={{fontSize:10,fontWeight:800,color:getBrandColors().green,textTransform:"uppercase",
                         letterSpacing:"0.05em",marginBottom:6}}>Recurring — Starting {billing.nextCycle}</div>
            <BRow label={`${CYCLE_LABEL[form.billing_cycle]} Program Fee`}
                  value={billing.discAmt>0?<><del style={{opacity:0.5,fontSize:11}}>${billing.gross.toFixed(2)}</del> ${billing.net.toFixed(2)}</>:`$${billing.net.toFixed(2)}`}/>
            {extraFees.filter(f=>f.recurring).map(f=>{
              const cm = CYCLE_MONTHS[form.billing_cycle] || 1;
              const perCycle = parseFloat(f.amount||0) * cm;
              return (
                <BRow key={f.id} label={f.label}
                      sub2={cm>1 ? `$${parseFloat(f.amount||0).toFixed(2)}/mo x ${cm} months` : undefined}
                      value={`$${perCycle.toFixed(2)}`} indented/>
              );
            })}
            {billing.recurDisc>0 && (
              <BRow label={`Special Discount${specialDisc.label?` — ${specialDisc.label}`:""}`}
                    value={`-$${billing.recurDisc.toFixed(2)}`} red/>
            )}
            <div style={{display:"flex",justifyContent:"space-between",paddingTop:6,marginTop:4}}>
              <span style={{fontSize:12,color:"#94a3b8"}}>Billed {CYCLE_LABEL[form.billing_cycle]}</span>
              <span style={{fontSize:14,fontWeight:800,color:getBrandColors().green}}>${billing.recurringNet.toFixed(2)}/{CYCLE_SHORT[form.billing_cycle]}</span>
            </div>
          </div>
        </div>

        <div style={{padding:"9px 12px",borderRadius:8,marginBottom:16,
                     background:"rgba(5,150,105,0.08)",border:"1px solid rgba(5,150,105,0.2)",
                     fontSize:12,color:isDark?"#6ee7b7":"#065f46",lineHeight:1.6}}>
          Clicking <strong>Confirm</strong> charges the customer and immediately activates their student record.
          This action cannot be undone.
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={handlePayApprove} disabled={paying}
            style={{flex:2,padding:"10px",borderRadius:8,border:"none",cursor:"pointer",
                    background:`linear-gradient(135deg,${getBrandColors().yellow},#e8b800)`,
                    color:getBrandColors().blue,fontSize:13,fontWeight:900,
                    display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                    opacity:paying?0.6:1}}>
            <FaCheck size={12}/> {paying?"Processing…":"Confirm — Pay & Activate"}
          </button>
          <button onClick={()=>setPayModal(false)}
            style={{flex:1,padding:"10px",borderRadius:8,
                    border:`1px solid ${isDark?"rgba(255,255,255,0.15)":"#e2e8f0"}`,
                    cursor:"pointer",background:"transparent",
                    color:isDark?"#94a3b8":"#64748b",fontSize:13}}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {showPayModal && <PayModal/>}
      <div style={{position:"fixed",top:0,right:0,bottom:0,width:540,zIndex:1000,
                   background:isDark?"#0f172a":"#f8fafc",
                   borderLeft:`2px solid ${isDark?"rgba(255,255,255,0.1)":"#e2e8f0"}`,
                   overflowY:"auto",boxShadow:"-8px 0 32px rgba(0,0,0,0.18)",
                   display:"flex",flexDirection:"column",
                   fontFamily:"'Segoe UI',system-ui,sans-serif"}}>

        {/* Header */}
        <div style={{padding:"14px 16px",background:getBrandColors().blue,display:"flex",
                     alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <div style={{fontSize:15,fontWeight:800,color:getBrandColors().yellow}}>{sub.first_name} {sub.last_name}</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.7)",marginTop:1}}>
              REG-{String(sub.submission_id).padStart(5,"0")} · {fmtDate(sub.submitted_at)}
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <StatusChip status={sub.status}/>
            <button onClick={onClose}
              style={{background:"rgba(255,255,255,0.12)",border:"none",borderRadius:6,
                      color:"#fff",cursor:"pointer",padding:"5px 9px",fontSize:13}}>
              <FaTimes/>
            </button>
          </div>
        </div>

        <div style={{padding:"14px 16px",flex:1}}>

          {/* Locked notice — final status, no further edits allowed */}
          {isLocked && (
            <div style={{padding:"9px 12px",borderRadius:8,marginBottom:10,fontSize:12,
                         display:"flex",alignItems:"center",gap:8,
                         background:isDark?"rgba(255,255,255,0.05)":"#f1f5f9",
                         color:isDark?"#94a3b8":"#64748b",
                         border:`1px solid ${isDark?"rgba(255,255,255,0.1)":"#e2e8f0"}`}}>
              <FaLock size={11}/> This registration is <strong>{sub.status}</strong> — record is final and read-only.
            </div>
          )}

          <fieldset disabled={isLocked} style={{border:"none",padding:0,margin:0}}>

          {/* Alert */}
          {msg && (
            <div style={{padding:"10px 12px",borderRadius:8,marginBottom:10,fontSize:12,
                         display:"flex",alignItems:"flex-start",gap:8,
                         background:msg.ok?"rgba(5,150,105,0.08)":"rgba(220,38,38,0.08)",
                         color:msg.ok?getBrandColors().green:getBrandColors().red,
                         border:`1px solid ${msg.ok?"#86efac":"#fca5a5"}`}}>
              {msg.ok?<FaCheckCircle size={12} style={{flexShrink:0,marginTop:1}}/>
                     :<FaExclamationCircle size={12} style={{flexShrink:0,marginTop:1}}/>}
              <span>{msg.text}</span>
            </div>
          )}

          {/* Parent Portal success card */}
          {approveResult && (
            <div style={{...card(getBrandColors().green),background:"rgba(5,150,105,0.06)"}}>
              <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:6}}>
                <FaKey size={11} color={getBrandColors().green}/>
                <span style={{fontSize:11,fontWeight:800,color:getBrandColors().green,textTransform:"uppercase",letterSpacing:"0.06em"}}>
                  Parent Portal Created
                </span>
              </div>
              <div style={{fontSize:12,color:isDark?"#e2e8f0":"#374151",lineHeight:1.8}}>
                Login: <strong>{approveResult.parent_portal_login}</strong><br/>
                Temp Password: <strong style={{letterSpacing:"0.06em"}}>Welcome2sixers</strong>
                <div style={{fontSize:11,color:"#94a3b8",marginTop:3}}>Parent must change password on first login.</div>
              </div>
            </div>
          )}

          {/* ── 1. Student Details ── */}
          <div style={card(getBrandColors().yellow)}>
            {secHead(<FaUser size={12}/>, "Student Details")}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 12px"}}>
              <div>{lbl("First Name",true)}
                <input value={form.first_name||""} onChange={e=>setF("first_name",e.target.value)} style={inp}/>
              </div>
              <div>{lbl("Last Name",true)}
                <input value={form.last_name||""} onChange={e=>setF("last_name",e.target.value)} style={inp}/>
              </div>
              <div>{lbl("Date of Birth")}
                <input type="date" value={(form.date_of_birth||"").slice(0,10)}
                  onChange={e=>setF("date_of_birth",e.target.value)} style={inp}/>
              </div>
              <div>{lbl("Gender")}
                <select value={form.gender||""} onChange={e=>setF("gender",e.target.value)} style={selInp}>
                  <option value="">—</option>
                  <option>Male</option><option>Female</option><option>Other</option>
                </select>
              </div>
              <div>{lbl("Skill Level")}
                <select value={form.skill_level||""} onChange={e=>setF("skill_level",e.target.value)} style={inp}>
                  <option value="">— select —</option>
                  {skillLevelOptions.map(l => <option key={l} value={l}>{l}</option>)}
                  {form.skill_level && !skillLevelOptions.includes(form.skill_level) && (
                    <option value={form.skill_level}>{form.skill_level}</option>
                  )}
                </select>
              </div>
              <div>{lbl("Playing Role")}
                <select value={form.playing_role||""} onChange={e=>setF("playing_role",e.target.value)} style={inp}>
                  <option value="">— select —</option>
                  {Object.entries(rolesByGroup).map(([grp, roles]) => (
                    <optgroup key={grp} label={grp}>
                      {roles.map(r => <option key={r} value={r}>{r}</option>)}
                    </optgroup>
                  ))}
                  {form.playing_role && !Object.values(rolesByGroup).flat().includes(form.playing_role) && (
                    <option value={form.playing_role}>{form.playing_role}</option>
                  )}
                </select>
              </div>
              <div style={{gridColumn:"1/-1"}}>{lbl("CricClub Player ID")}
                <input value={form.cricclub_player_id||""} onChange={e=>setF("cricclub_player_id",e.target.value)}
                  style={inp} placeholder="Optional — e.g. CC-12345"/>
              </div>
            </div>
            {/* Address */}
            <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${isDark?"rgba(255,255,255,0.08)":"#f1f5f9"}`}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                <FaMapMarkerAlt size={10} color="#94a3b8"/>
                <span style={{fontSize:10,fontWeight:800,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em"}}>Address</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 12px"}}>
                <div style={{gridColumn:"1/-1"}}>{lbl("Street Address")}
                  <input value={form.street_address||""} onChange={e=>setF("street_address",e.target.value)} style={inp}/>
                </div>
                <div>{lbl("City")}
                  <input value={form.city||""} onChange={e=>setF("city",e.target.value)} style={inp}/>
                </div>
                <div>{lbl("State")}
                  <input value={form.state||""} onChange={e=>setF("state",e.target.value)} style={inp}/>
                </div>
                <div>{lbl("Zip / Postal Code")}
                  <input value={form.zip_code||""} onChange={e=>setF("zip_code",e.target.value)} style={inp}/>
                </div>
              </div>
            </div>
          </div>

          {/* ── 2. Parent / Guardian ── */}
          <div style={card("#a78bfa")}>
            {secHead(<FaUserFriends size={12}/>, "Parent / Guardian")}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 12px"}}>
              <div style={{gridColumn:"1/-1"}}>{lbl("Full Name",true)}
                <input value={form.parent_name||""} onChange={e=>setF("parent_name",e.target.value)} style={inp}/>
              </div>
              <div>{lbl("Email Address",true)}
                <input value={form.parent_email||""} onChange={e=>setF("parent_email",e.target.value)} style={inp}/>
              </div>
              <div>{lbl("Phone")}
                <input value={form.parent_phone||""} onChange={e=>setF("parent_phone",e.target.value)} style={inp}/>
              </div>
            </div>
            <div style={{marginTop:10,padding:"9px 12px",borderRadius:8,
                         background:"rgba(167,139,250,0.07)",border:"1px solid rgba(167,139,250,0.2)",
                         display:"flex",alignItems:"flex-start",gap:8}}>
              <FaKey size={12} color="#a78bfa" style={{flexShrink:0,marginTop:2}}/>
              <div style={{fontSize:12,color:isDark?"#c4b5fd":"#5b21b6",lineHeight:1.8}}>
                <strong>Parent Portal</strong> — Login:{" "}
                <code style={{background:"rgba(167,139,250,0.15)",padding:"1px 5px",borderRadius:3}}>{form.parent_email||"(email)"}</code>
                &nbsp;·&nbsp; Temp Password:{" "}
                <code style={{background:"rgba(167,139,250,0.15)",padding:"1px 5px",borderRadius:3}}>Welcome2sixers</code>
                <div style={{fontSize:11,opacity:0.8,marginTop:3}}>Created on approval. Parent must change on first login.</div>
              </div>
            </div>
          </div>

          {/* ── Payment Card (from registration or parent account) ── */}
          {(form.stripe_pm_id || form.stripe_card_last4) ? (
            <div style={card("#38bdf8")}>
              {secHead(<FaCreditCard size={12}/>, "Payment Card", "Card on file for this parent")}
              <div style={{display:"flex",alignItems:"center",gap:14,padding:"12px 16px",
                           borderRadius:10,background:"rgba(56,189,248,0.07)",
                           border:"1px solid rgba(56,189,248,0.25)"}}>
                <div style={{
                  width:50,height:32,borderRadius:6,background:"#0c4a6e",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:10,fontWeight:800,color:"#fff",flexShrink:0,letterSpacing:0.5,
                }}>
                  {form.stripe_card_brand
                    ? form.stripe_card_brand.toUpperCase().slice(0,4)
                    : <FaCreditCard size={14}/>}
                </div>
                <div>
                  <div style={{fontWeight:700,fontSize:15,color:isDark?"#e2e8f0":"#0c4a6e"}}>
                    •••• •••• •••• {form.stripe_card_last4 || "????"}
                  </div>
                  {form.stripe_card_exp_month && form.stripe_card_exp_year && (
                    <div style={{fontSize:12,color:isDark?"#94a3b8":"#64748b",marginTop:2}}>
                      Expires {String(form.stripe_card_exp_month).padStart(2,"0")}/{String(form.stripe_card_exp_year).slice(-2)}
                    </div>
                  )}
                </div>
                <span style={{
                  marginLeft:"auto",padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,
                  background:"rgba(5,150,105,0.12)",color:getBrandColors().green,border:"1px solid rgba(5,150,105,0.3)",
                }}>✓ Verified</span>
              </div>
              <div style={{fontSize:11,color:isDark?"#94a3b8":"#64748b",marginTop:8}}>
                Card is saved with Stripe and will be linked to this parent account on approval.
              </div>
            </div>
          ) : (
            <div style={card("#38bdf8")}>
              {/* Header row: title + security badges (mirrors registration form) */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10, gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <FaCreditCard size={12} color="#38bdf8"/>
                  <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b" }}>Payment Card</span>
                  <span style={{ fontSize: 11, color: isDark ? "#64748b" : "#94a3b8", marginLeft: 4 }}>— No card captured yet</span>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {[
                    { icon: <FaShieldAlt size={11} color="#22c55e"/>, label: "AES-256", sub: "Encrypted" },
                    { icon: <FaLock      size={11} color="#60a5fa"/>, label: "PCI DSS", sub: "Certified"  },
                    { icon: <FaIdCard   size={11} color="#a78bfa"/>, label: "Zero Storage", sub: "Token only" },
                  ].map((b, i) => (
                    <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center",
                      padding: "5px 8px", borderRadius: 8, minWidth: 64, textAlign: "center",
                      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.04)",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.08)"}` }}>
                      {b.icon}
                      <div style={{ fontSize: 9, fontWeight: 800, color: isDark ? "#94a3b8" : "#64748b", lineHeight: 1.3, marginTop: 3 }}>{b.label}</div>
                      <div style={{ fontSize: 9, color: isDark ? "#64748b" : "#94a3b8", lineHeight: 1.2 }}>{b.sub}</div>
                    </div>
                  ))}
                </div>
              </div>

              {cardMsg && (
                <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 8, fontSize: 12,
                  background: cardMsg.ok ? "rgba(5,150,105,0.1)" : "rgba(220,38,38,0.08)",
                  color: cardMsg.ok ? getBrandColors().green : getBrandColors().red, border: `1px solid ${(cardMsg.ok ? getBrandColors().green : getBrandColors().red)}40` }}>
                  {cardMsg.ok ? "✓" : "✗"} {cardMsg.text}
                </div>
              )}

              {!showCardCapture ? (
                <div>
                  <div style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b", marginBottom: 10 }}>
                    No card on file for <strong>{form.parent_email}</strong>. The parent did not provide card details during registration.
                  </div>
                  {STRIPE_PK ? (
                    <button onClick={() => { setShowCardCapture(true); setCardMsg(null); }}
                      style={{ padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
                        background: "#38bdf8", color: "#fff", fontSize: 12, fontWeight: 700 }}>
                      + Capture Card Now
                    </button>
                  ) : (
                    <div style={{ fontSize: 11, color: getBrandColors().orange, padding: "8px 12px", borderRadius: 8,
                      background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.2)" }}>
                      Stripe not configured — add <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> to enable card capture.
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b", marginBottom: 8 }}>
                    Enter card details for <strong>{form.parent_name}</strong> ({form.parent_email}).
                    Card number is sent directly to Stripe — never stored in our database.
                  </div>
                  {/* Stripe-hosted card element */}
                  <div id="approval-card-el" style={{
                    padding: "11px 14px", borderRadius: 8,
                    border: `1.5px solid ${isDark ? "rgba(56,189,248,0.4)" : "#94d3f0"}`,
                    background: isDark ? "rgba(255,255,255,0.06)" : "#f8fbff",
                    minHeight: 42, marginBottom: 10,
                  }} />
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button onClick={handleSaveCard} disabled={cardSaving}
                      style={{ padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer",
                        background: getBrandColors().green, color: "#fff", fontSize: 12, fontWeight: 700,
                        opacity: cardSaving ? 0.6 : 1 }}>
                      {cardSaving ? "Saving…" : "Save Card"}
                    </button>
                    <button onClick={() => { setShowCardCapture(false); setCardMsg(null); }}
                      style={{ padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                        background: "transparent", border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1"}`,
                        color: isDark ? "#94a3b8" : "#64748b", fontSize: 12 }}>
                      Cancel
                    </button>
                    <span style={{ fontSize: 11, color: isDark ? "#64748b" : "#94a3b8", marginLeft: 4 }}>
                      <FaLock size={9} style={{ marginRight: 4 }}/>Secured by Stripe
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── 3. Coaching Program ── */}
          <div style={card(getBrandColors().green)}>
            {secHead(<FaGraduationCap size={12}/>, "Coaching Program", "Admin can update program and billing cycle")}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 12px",marginBottom:8}}>
              {multiYear && (
                <div style={{gridColumn:"1/-1"}}>
                  {lbl("Filter by Year")}
                  <select value={progYear} onChange={e=>{ setProgYear(e.target.value); setF("program_id",""); setF("program_name",""); }} style={selInp}>
                    <option value="">— All Years —</option>
                    {progYears.map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              )}
              <div style={{gridColumn:"1/-1"}}>
                {lbl("Program")}
                <select value={form.program_id||""} onChange={e=>selectProgram(e.target.value)} style={selInp}>
                  <option value="">— Select Program —</option>
                  {filteredPrograms.map(p=>(
                    <option key={p.program_id} value={p.program_id}>
                      {p.program_name}{p.level_name?` (${p.level_name})`:""}
                      {p.valid_from?` · ${String(p.valid_from).slice(0,4)}`:""}
                    </option>
                  ))}
                </select>
                {form.program_id && !program && (
                  <div style={{fontSize:11,color:"#f59e0b",marginTop:3}}>
                    ⚠ Program not found in active list — fee data unavailable
                  </div>
                )}
              </div>
              <div>
                {lbl("Billing Cycle")}
                <select value={form.billing_cycle||"quarterly"}
                  onChange={e=>setF("billing_cycle",e.target.value)} style={selInp}>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi_annual">Semi-Annual</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              {program && (
                <div>
                  {lbl("Level")}
                  <input value={program.level_name||"—"} readOnly
                    style={{...inp,opacity:0.6,cursor:"default"}}/>
                </div>
              )}
            </div>
            {/* Live program fee display */}
            {program && (()=>{
              const { gross, discAmt, discLabel, net } = getProgramPricing(program, form.billing_cycle||"quarterly");
              return gross>0 ? (
                <div style={{padding:"8px 11px",borderRadius:8,
                             background:"rgba(52,211,153,0.07)",border:"1px solid rgba(52,211,153,0.2)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                    <span style={{color:"#94a3b8"}}>{CYCLE_LABEL[form.billing_cycle||"quarterly"]} fee</span>
                    <span style={{fontWeight:700,color:getBrandColors().green}}>
                      {discAmt>0
                        ? <><del style={{opacity:0.5,fontWeight:400}}>${gross.toFixed(2)}</del> ${net.toFixed(2)}</>
                        : `$${net.toFixed(2)}`}
                      <span style={{fontWeight:400,color:"#94a3b8",marginLeft:3}}>/{CYCLE_SHORT[form.billing_cycle||"quarterly"]}</span>
                    </span>
                  </div>
                  {discAmt>0&&<div style={{fontSize:11,color:getBrandColors().green,marginTop:2}}>Discount: {discLabel} off (${discAmt.toFixed(2)})</div>}
                </div>
              ) : null;
            })()}
          </div>

          {/* ── 4. Waiver ── */}
          <div style={card(getBrandColors().red)}>
            {secHead(<FaFileSignature size={12}/>, "Waiver")}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                {sub.waiver_signed
                  ? <><FaCheckCircle color={getBrandColors().green} size={14}/><span style={{fontSize:12,color:getBrandColors().green}}>Signed — {fmtDate(sub.waiver_signed_at)}</span></>
                  : <><FaExclamationCircle color={getBrandColors().red} size={14}/><span style={{fontSize:12,color:getBrandColors().red}}>Not signed</span></>}
              </div>
              {sub.signed_pdf_name && (
                <a href={`${BASE}/waivers/signed/${sub.signed_pdf_name}`} target="_blank" rel="noreferrer"
                  style={{display:"flex",alignItems:"center",gap:5,fontSize:11,fontWeight:700,
                          color:getBrandColors().blue,textDecoration:"none",padding:"4px 10px",borderRadius:6,
                          background:isDark?"rgba(3,78,123,0.2)":"#eff6ff",border:`1px solid ${getBrandColors().blue}33`}}>
                  <FaDownload size={10}/> View PDF
                </a>
              )}
            </div>
          </div>

          {/* ── 5. Billing & Overrides ── */}
          <div style={card("#f59e0b")}>
            {secHead(<FaReceipt size={12}/>, "Billing Overrides & Summary",
              "Live — updates as you change values below")}

            {/* Admin fee override */}
            <div style={{display:"grid",gridTemplateColumns:"1fr auto",alignItems:"center",gap:10,marginBottom:10}}>
              <div>{lbl("Administrative Fee (one-time, overridable)")}
                <div style={{fontSize:11,color:"#94a3b8"}}>Default ${DEFAULT_ADMIN_FEE.toFixed(2)}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:13,color:"#94a3b8"}}>$</span>
                <input type="number" min={0} step="0.01"
                  value={adminFee} onChange={e=>setAdminFee(parseFloat(e.target.value)||0)}
                  style={{...numInp,width:80}}/>
              </div>
            </div>

            {/* Additional fees */}
            <div style={{marginBottom:10}}>
              <div style={{fontSize:10,fontWeight:800,color:isDark?"#94a3b8":"#64748b",
                           textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>
                Additional Fees
              </div>
              {extraFees.map(f=>(
                <div key={f.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,
                                        padding:"6px 10px",borderRadius:7,
                                        background:isDark?"rgba(255,255,255,0.04)":"#f8fafc",
                                        border:`1px solid ${isDark?"rgba(255,255,255,0.08)":"#e2e8f0"}`}}>
                  <span style={{flex:1,fontSize:12,color:isDark?"#e2e8f0":"#374151"}}>{f.label}</span>
                  <span style={{fontSize:12,fontWeight:700,color:isDark?"#f1f5f9":"#1e293b"}}>${parseFloat(f.amount).toFixed(2)}</span>
                  {f.recurring&&(
                    <span style={{fontSize:10,padding:"2px 6px",borderRadius:10,
                                   background:"rgba(52,211,153,0.12)",color:getBrandColors().green,fontWeight:700}}>Recurring</span>
                  )}
                  <button onClick={()=>removeFee(f.id)}
                    style={{background:"none",border:"none",cursor:"pointer",color:"#ef4444",padding:0,lineHeight:1}}>
                    <FaTrash size={10}/>
                  </button>
                </div>
              ))}
              {/* Add fee row */}
              <div style={{display:"flex",gap:6,alignItems:"flex-end"}}>
                <div style={{flex:2}}>
                  <input value={newFeeLabel} onChange={e=>setNewFeeLabel(e.target.value)}
                    placeholder="Fee label (e.g. Jersey Fee)"
                    style={{...inp,fontSize:12,padding:"6px 9px"}}
                    onKeyDown={e=>e.key==="Enter"&&addFee()}/>
                </div>
                <div style={{flex:0,minWidth:80}}>
                  <input type="number" min={0} step="0.01" value={newFeeAmt}
                    onChange={e=>setNewFeeAmt(e.target.value)}
                    placeholder="$ Amt"
                    style={{...inp,fontSize:12,padding:"6px 9px",textAlign:"right"}}
                    onKeyDown={e=>e.key==="Enter"&&addFee()}/>
                </div>
                <label style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#94a3b8",
                               whiteSpace:"nowrap",cursor:"pointer",paddingBottom:2}}>
                  <input type="checkbox" checked={newFeeRecur} onChange={e=>setNewFeeRecur(e.target.checked)}
                    style={{accentColor:getBrandColors().green}}/>
                  Recurring
                </label>
                <button onClick={addFee}
                  style={{padding:"6px 10px",borderRadius:7,border:"none",cursor:"pointer",
                          background:getBrandColors().blue,color:"#fff",fontSize:12,display:"flex",alignItems:"center",gap:4}}>
                  <FaPlus size={9}/> Add
                </button>
              </div>
            </div>

            {/* Special discount */}
            <div style={{marginBottom:12,padding:"10px 12px",borderRadius:8,
                         background:isDark?"rgba(239,68,68,0.06)":"rgba(239,68,68,0.04)",
                         border:`1px solid rgba(239,68,68,0.15)`}}>
              <div style={{fontSize:10,fontWeight:800,color:"#ef4444",textTransform:"uppercase",
                           letterSpacing:"0.05em",marginBottom:8}}>Student-Level Special Discount</div>
              {/* $ vs % toggle */}
              <div style={{display:"flex",gap:4,marginBottom:8}}>
                {[{v:"amt",icon:"$",lbl:"Fixed ($)"},{v:"pct",icon:"%",lbl:"Percent (%)"}].map(opt=>(
                  <button key={opt.v} onClick={()=>setSpecialDisc(d=>({...d,discType:opt.v,amount:""}))}
                    style={{flex:1,padding:"5px 0",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,
                            background:specialDisc.discType===opt.v?"#ef4444":"rgba(239,68,68,0.1)",
                            color:specialDisc.discType===opt.v?"#fff":"#ef4444",transition:"all 0.15s"}}>
                    {opt.icon} {opt.lbl}
                  </button>
                ))}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 110px",gap:"8px 10px",alignItems:"end"}}>
                <div>{lbl("Discount Label / Reason")}
                  <input value={specialDisc.label} onChange={e=>setSpecialDisc(d=>({...d,label:e.target.value}))}
                    placeholder="e.g. Sibling Discount, Scholarship"
                    style={{...inp,fontSize:12,padding:"6px 9px"}}/>
                </div>
                <div>{lbl(specialDisc.discType==="pct"?"Percentage (%)":"Amount ($)")}
                  <div style={{position:"relative"}}>
                    <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",
                                  fontSize:12,color:"#94a3b8",pointerEvents:"none"}}>
                      {specialDisc.discType==="pct"?"%":"$"}
                    </span>
                    <input type="number" min={0} step={specialDisc.discType==="pct"?"0.1":"0.01"}
                      max={specialDisc.discType==="pct"?100:undefined}
                      value={specialDisc.amount}
                      onChange={e=>setSpecialDisc(d=>({...d,amount:e.target.value}))}
                      style={{...inp,fontSize:12,padding:"6px 9px 6px 22px",textAlign:"right"}}/>
                  </div>
                  {specialDisc.discType==="pct" && specialDisc.amount && billing.firstDisc>0 && (
                    <div style={{fontSize:10,color:"#ef4444",marginTop:2}}>
                      = ${billing.firstDisc.toFixed(2)} off first payment
                    </div>
                  )}
                </div>
              </div>
              <label style={{display:"flex",alignItems:"center",gap:6,marginTop:8,cursor:"pointer",
                             fontSize:12,color:isDark?"#94a3b8":"#64748b"}}>
                <input type="checkbox" checked={specialDisc.recurring}
                  onChange={e=>setSpecialDisc(d=>({...d,recurring:e.target.checked}))}
                  style={{accentColor:getBrandColors().green}}/>
                Apply as <strong style={{marginLeft:3}}>recurring discount</strong> (deducted every billing cycle)
              </label>
            </div>

            {/* Live billing summary */}
            <div style={{padding:"12px 14px",borderRadius:9,
                         background:isDark?"rgba(245,158,11,0.07)":"rgba(245,158,11,0.06)",
                         border:"1px solid rgba(245,158,11,0.25)"}}>
              <div style={{fontSize:10,fontWeight:800,color:"#f59e0b",textTransform:"uppercase",
                           letterSpacing:"0.05em",marginBottom:10}}>
                <FaCalendarAlt size={9} style={{marginRight:5}}/>First Payment (Due on Approval)
              </div>
              <BRow label={`Prorated — ${billing.curMonth} (${billing.remainingSessions}/${billing.totalSessions} sessions @ $${billing.monthlyNet.toFixed(2)}/mo)`}
                    value={`$${billing.prorated.toFixed(2)}`}/>
              <BRow label="Administrative Fee" value={`$${billing.adminFeeAmt.toFixed(2)}`}/>
              {extraFees.filter(f=>!f.recurring).map(f=>(
                <BRow key={f.id} label={f.label} value={`$${parseFloat(f.amount||0).toFixed(2)}`} indented/>
              ))}
              {billing.firstDisc>0 && (
                <BRow label={`Special Discount${specialDisc.label?` — ${specialDisc.label}`:""}`}
                      value={`-$${billing.firstDisc.toFixed(2)}`} red/>
              )}
              <div style={{display:"flex",justifyContent:"space-between",
                           paddingTop:9,marginTop:4,
                           borderTop:`2px solid ${isDark?"rgba(255,255,255,0.12)":"#e2e8f0"}`}}>
                <span style={{fontSize:14,fontWeight:800,color:isDark?"#f1f5f9":getBrandColors().blue}}>Total Charged Now</span>
                <span style={{fontSize:20,fontWeight:900,color:isDark?getBrandColors().yellow:getBrandColors().blue}}>${billing.totalNow.toFixed(2)}</span>
              </div>

              <div style={{marginTop:12,paddingTop:10,
                           borderTop:`1px solid ${isDark?"rgba(255,255,255,0.07)":"#f1f5f9"}`}}>
                <div style={{fontSize:10,fontWeight:800,color:getBrandColors().green,textTransform:"uppercase",
                             letterSpacing:"0.05em",marginBottom:7}}>
                  <FaDollarSign size={9} style={{marginRight:4}}/>Recurring — Starting {billing.nextCycle}
                </div>
                <BRow label={`${CYCLE_LABEL[form.billing_cycle||"quarterly"]} Program Fee`}
                      value={`$${billing.gross.toFixed(2)}`} strike={billing.discAmt>0}/>
                {billing.discAmt>0 && (
                  <BRow label={`Program Discount (${billing.discLabel})`}
                        value={`-$${billing.discAmt.toFixed(2)}`} red indented/>
                )}
                {extraFees.filter(f=>f.recurring).map(f=>(
                  <BRow key={f.id} label={f.label} value={`$${parseFloat(f.amount||0).toFixed(2)}`} indented/>
                ))}
                {billing.recurDisc>0 && (
                  <BRow label={`Student Discount — ${specialDisc.label||"Special"}`}
                        value={`-$${billing.recurDisc.toFixed(2)}`} red/>
                )}
                <div style={{display:"flex",justifyContent:"space-between",paddingTop:7,marginTop:4}}>
                  <span style={{fontSize:12,color:"#94a3b8"}}>Billed {CYCLE_LABEL[form.billing_cycle||"quarterly"]}</span>
                  <span style={{fontSize:15,fontWeight:800,color:getBrandColors().green}}>${billing.recurringNet.toFixed(2)}/{CYCLE_SHORT[form.billing_cycle||"quarterly"]}</span>
                </div>
              </div>
            </div>

            <div style={{marginTop:8,fontSize:11,color:"#94a3b8",padding:"7px 10px",borderRadius:7,
                         background:isDark?"rgba(255,255,255,0.03)":"#fafafa",
                         border:`1px solid ${isDark?"rgba(255,255,255,0.06)":"#f1f5f9"}`}}>
              ⚠ <strong>Admin action required</strong> after activation: configure recurring billing schedule
              in the billing module with the amounts shown above.
            </div>
          </div>

          {/* ── 6. Notes ── */}
          <div style={card("#60a5fa")}>
            {secHead(<FaStickyNote size={12}/>, "Admin Notes", "Internal — not visible to parent")}
            <textarea value={form.notes||""} onChange={e=>setF("notes",e.target.value)}
              rows={3} placeholder="Add internal notes, observations or instructions for the team…"
              style={{...inp,resize:"vertical",minHeight:70,fontFamily:"inherit"}}/>
          </div>

          {/* Rejection reason (read-only) */}
          {sub.status==="rejected" && sub.rejection_reason && (
            <div style={{padding:"9px 12px",borderRadius:8,marginBottom:10,
                         background:"rgba(220,38,38,0.06)",border:"1px solid rgba(220,38,38,0.2)"}}>
              <div style={{fontSize:10,fontWeight:700,color:getBrandColors().red,marginBottom:3}}>REJECTION REASON</div>
              <div style={{fontSize:12,color:"#991b1b"}}>{sub.rejection_reason}</div>
            </div>
          )}

          {/* Reject input */}
          {showReject && (
            <div style={{...card(getBrandColors().red),background:"rgba(220,38,38,0.05)",marginBottom:10}}>
              {lbl("Reason for Rejection *")}
              <textarea value={rejectReason} onChange={e=>setRejectReason(e.target.value)}
                rows={2} placeholder="Enter reason for rejecting this registration…"
                style={{...inp,resize:"vertical",background:isDark?"rgba(255,255,255,0.07)":"#fff",marginBottom:8}}/>
              <div style={{display:"flex",gap:8}}>
                <button onClick={handleReject}
                  style={{flex:1,padding:"7px",borderRadius:7,border:"none",cursor:"pointer",
                          background:getBrandColors().red,color:"#fff",fontSize:12,fontWeight:700}}>
                  Confirm Reject
                </button>
                <button onClick={()=>setShowReject(false)}
                  style={{padding:"7px 14px",borderRadius:7,border:"none",cursor:"pointer",
                          background:isDark?"rgba(255,255,255,0.1)":"#e2e8f0",
                          color:isDark?"#e2e8f0":"#374151",fontSize:12}}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Approved badge */}
          {sub.status==="approved" && sub.student_id && (
            <div style={{padding:"9px 12px",borderRadius:8,marginBottom:10,
                         background:"rgba(5,150,105,0.07)",border:"1px solid rgba(5,150,105,0.2)"}}>
              <div style={{fontSize:10,fontWeight:700,color:getBrandColors().green,marginBottom:3}}>APPROVED</div>
              <div style={{fontSize:12,color:isDark?"#6ee7b7":"#065f46"}}>
                Student #{sub.student_id} activated by {sub.reviewed_by} on {fmtDate(sub.reviewed_at)}
                {sub.total_paid&&<span style={{marginLeft:8,fontWeight:700}}>· ${Number(sub.total_paid).toFixed(2)} charged</span>}
              </div>
            </div>
          )}

          {/* Post-rejection next-steps panel */}
          {sub.status==="rejected" && (
            <div style={{padding:"12px 14px",borderRadius:10,marginBottom:10,
                         background:"rgba(220,38,38,0.05)",border:"1px solid rgba(220,38,38,0.2)"}}>
              <div style={{fontSize:11,fontWeight:800,color:getBrandColors().red,marginBottom:6,letterSpacing:1}}>
                NEXT STEPS — REJECTED REGISTRATION
              </div>
              <div style={{fontSize:12,color:isDark?"#fca5a5":"#991b1b",lineHeight:1.8}}>
                <div>1. Parent has been notified via email (if configured).</div>
                <div>2. You may contact the parent to discuss the reason for rejection.</div>
                <div>3. Parent can re-submit a new registration after resolving the issue.</div>
                <div>4. This record is retained for audit purposes and cannot be deleted.</div>
              </div>
            </div>
          )}

          </fieldset>

          {/* Actions */}
          <div style={{display:"flex",gap:8,marginTop:4}}>
            {!isLocked && (
              <button onClick={handleSave} disabled={saving}
                style={{flex:1,padding:"9px",borderRadius:8,border:"none",cursor:"pointer",
                        background:isDark?"rgba(255,255,255,0.1)":"#f1f5f9",
                        color:isDark?"#e2e8f0":"#374151",fontSize:12,fontWeight:700,
                        display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                        opacity:saving?0.6:1}}>
                <FaSave size={11}/> {saving?"Saving…":"Save Changes"}
              </button>
            )}
            {sub.status==="pending" && (
              <>
                <button onClick={()=>{ setPayModal(true); setMsg(null); }}
                  style={{flex:2,padding:"9px",borderRadius:8,border:"none",cursor:"pointer",
                          background:`linear-gradient(135deg,${getBrandColors().yellow},#e8b800)`,
                          color:getBrandColors().blue,fontSize:13,fontWeight:900,
                          display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
                  <FaCheck size={12}/> Pay &amp; Approve
                </button>
                <button onClick={()=>{ setShowReject(v=>!v); setMsg(null); }}
                  style={{padding:"9px 13px",borderRadius:8,border:"none",cursor:"pointer",
                          background:"rgba(220,38,38,0.08)",color:getBrandColors().red,fontSize:12,fontWeight:700,
                          display:"flex",alignItems:"center",gap:6}}>
                  <FaTimes size={11}/> Reject
                </button>
              </>
            )}
            {sub.status==="rejected" && (
              <div style={{flex:1,padding:"9px 13px",borderRadius:8,fontSize:12,fontWeight:700,
                           background:"rgba(220,38,38,0.08)",color:getBrandColors().red,
                           display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                           border:"1px solid rgba(220,38,38,0.2)"}}>
                <FaTimesCircle size={13}/> Rejected
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Main Approval Queue ──────────────────────────────────────────────────── */
export default function RegistrationApproval({ currentUser }) {
  const { isDark } = useTheme();
  const T          = getT(isDark);
  const { fmtDate } = useDateTimeFormat();

  const [activeTab,    setActiveTab]    = useState("pending");
  const [submissions,  setSubmissions]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [selected,     setSelected]     = useState(null);
  const [counts,       setCounts]       = useState({ pending:0, approved:0, rejected:0 });
  const [programsList, setProgramsList] = useState([]);

  // Load programs once for the dropdown
  useEffect(() => {
    fetch(`${BASE}/coaching-programs/`)
      .then(r=>r.json())
      .then(d=>setProgramsList(d.programs||[]))
      .catch(()=>{});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const res  = await fetch(`${BASE}/registration/?status=${activeTab}`);
    const data = await res.json();
    setSubmissions(data.submissions||[]);
    setLoading(false);
  }, [activeTab]);

  useEffect(() => { load(); setSelected(null); }, [load]);

  // Lightweight counts — single aggregation query, no row data
  const loadCounts = useCallback(() => {
    fetch(`${BASE}/registration/counts`)
      .then(r=>r.json())
      .then(d=>setCounts({ pending:d.pending||0, approved:d.approved||0, rejected:d.rejected||0 }))
      .catch(()=>{});
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts, submissions]);

  const filtered = submissions.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (s.first_name||"").toLowerCase().includes(q) ||
      (s.last_name ||"").toLowerCase().includes(q) ||
      (s.parent_name||"").toLowerCase().includes(q) ||
      (s.parent_email||"").toLowerCase().includes(q)
    );
  });

  const cardBg = isDark?"rgba(255,255,255,0.04)":"#fff";
  const border  = isDark?"rgba(255,255,255,0.1)":"#e2e8f0";

  return (
    <div style={{padding:"20px 24px"}}>

      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20,
                   padding:"14px 18px",borderRadius:12,
                   background:"rgba(255,255,255,0.15)",backdropFilter:"blur(14px)", WebkitBackdropFilter: "blur(14px)",
                   border:"1px solid rgba(255,255,255,0.25)",width:"fit-content"}}>
        <FaClipboardList size={22} color={getBrandColors().blue}/>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:isDark?"#f1f5f9":getBrandColors().blue}}>Registration Approval Queue</div>
          <div style={{fontSize:12,color:T.textMuted||"#94a3b8"}}>Review, collect payment and activate students</div>
        </div>
      </div>

      {/* KPI chips */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16,maxWidth:480}}>
        {TABS.map(t=>{
          const m = STATUS_META[t];
          return (
            <div key={t} onClick={()=>setActiveTab(t)}
              style={{background:activeTab===t?m.bg:cardBg,
                      border:`1.5px solid ${activeTab===t?m.color+"60":border}`,
                      borderRadius:10,padding:"10px 14px",cursor:"pointer",transition:"all 0.15s"}}>
              <div style={{fontSize:11,color:m.color,fontWeight:700,marginBottom:3}}>{m.label}</div>
              <div style={{fontSize:22,fontWeight:800,color:isDark?"#f1f5f9":"#1e293b"}}>{counts[t]}</div>
            </div>
          );
        })}
      </div>

      {/* Tab bar + search */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                   marginBottom:12,gap:12,flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:6}}>
          {TABS.map(t=>{
            const m=STATUS_META[t]; const active=activeTab===t;
            return (
              <button key={t} onClick={()=>setActiveTab(t)}
                style={{padding:"6px 14px",borderRadius:20,border:"none",cursor:"pointer",
                        fontSize:12,fontWeight:700,
                        background:active?`linear-gradient(135deg,${getBrandColors().yellow},#e8b800)`:(isDark?"rgba(255,255,255,0.08)":"#f1f5f9"),
                        color:active?getBrandColors().blue:(isDark?"#94a3b8":"#64748b")}}>
                {m.label}
                {counts[t]>0&&<span style={{marginLeft:4,padding:"1px 6px",borderRadius:10,
                  background:active?getBrandColors().blue+"30":(isDark?"rgba(255,255,255,0.1)":"#e2e8f0"),
                  color:active?getBrandColors().blue:"#94a3b8",fontSize:10}}>{counts[t]}</span>}
              </button>
            );
          })}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={()=>{ load(); loadCounts(); }}
            style={{padding:"6px 14px",borderRadius:8,border:`1px solid ${border}`,cursor:"pointer",
                    background:cardBg,color:isDark?"#94a3b8":"#64748b",
                    fontSize:12,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
            ↻ Refresh All
          </button>
          <div style={{display:"flex",alignItems:"center",gap:8,background:cardBg,
                       border:`1px solid ${border}`,borderRadius:8,padding:"6px 12px"}}>
            <FaSearch size={11} color="#94a3b8"/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search by name or email…"
              style={{border:"none",outline:"none",background:"transparent",
                      fontSize:13,color:T.textPrimary||"#1e293b",width:200}}/>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{background:cardBg,border:`1px solid ${border}`,borderRadius:12,overflow:"hidden"}}>
        <div style={{display:"grid",gridTemplateColumns:"180px 1fr 160px 110px 100px 90px",
                     padding:"8px 16px",borderBottom:`1px solid ${border}`,
                     background:isDark?"rgba(255,255,255,0.03)":"#f8fafc"}}>
          {["Student","Parent / Email","Program","Waiver","Submitted",""].map(h=>(
            <div key={h} style={{fontSize:10,fontWeight:800,color:"#94a3b8",
                                 textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</div>
          ))}
        </div>
        {loading ? (
          <div style={{padding:"32px",textAlign:"center",color:"#94a3b8",fontSize:13}}>Loading…</div>
        ) : filtered.length===0 ? (
          <div style={{padding:"32px",textAlign:"center"}}>
            <FaClipboardList size={28} color="#94a3b8" style={{marginBottom:8,opacity:0.4}}/>
            <div style={{color:"#94a3b8",fontSize:13}}>
              {search?"No matches found.":`No ${activeTab} registrations.`}
            </div>
          </div>
        ) : filtered.map((s,i)=>(
          <div key={s.submission_id} onClick={()=>{
            // Set from list immediately so panel opens instantly, then enrich with full data
            setSelected(s);
            fetch(`${BASE}/registration/${s.submission_id}`)
              .then(r=>r.json())
              .then(d=>{ if(d.submission) setSelected(d.submission); })
              .catch(()=>{});
          }}
            style={{display:"grid",gridTemplateColumns:"180px 1fr 160px 110px 100px 90px",
                    padding:"10px 16px",cursor:"pointer",
                    borderBottom:i<filtered.length-1?`1px solid ${border}`:"none",
                    background:selected?.submission_id===s.submission_id
                      ?(isDark?"rgba(3,78,123,0.15)":"#eff6ff")
                      :(i%2===0?"transparent":(isDark?"rgba(255,255,255,0.015)":"#fafafa")),
                    transition:"background 0.12s"}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:isDark?"#f1f5f9":"#1e293b"}}>
                {s.first_name} {s.last_name}
              </div>
              <div style={{fontSize:10,color:"#94a3b8"}}>REG-{String(s.submission_id).padStart(5,"0")}</div>
            </div>
            <div>
              <div style={{fontSize:12,color:isDark?"#e2e8f0":"#374151"}}>{s.parent_name}</div>
              <div style={{fontSize:10,color:"#94a3b8"}}>{s.parent_email}</div>
            </div>
            <div style={{fontSize:12,color:isDark?"#e2e8f0":"#374151"}}>
              {s.program_name||"—"}
              {s.billing_cycle&&<span style={{fontSize:10,color:"#94a3b8",marginLeft:4}}>({s.billing_cycle})</span>}
            </div>
            <div style={{display:"flex",alignItems:"center"}}>
              {s.waiver_signed
                ?<span style={{fontSize:11,color:getBrandColors().green,fontWeight:700,display:"flex",gap:4,alignItems:"center"}}><FaCheckCircle size={10}/> Signed</span>
                :<span style={{fontSize:11,color:getBrandColors().red,fontWeight:700}}>Not signed</span>}
            </div>
            <div style={{fontSize:11,color:"#94a3b8"}}>{fmtDate(s.submitted_at)}</div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:6}}>
              <StatusChip status={s.status}/>
              <FaEye size={12} color="#94a3b8"/>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <>
          <div onClick={()=>setSelected(null)}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.3)",zIndex:999}}/>
          <DetailPanel
            sub={selected}
            currentUser={currentUser}
            programsList={programsList}
            onClose={()=>setSelected(null)}
            onRefresh={()=>{
              load();
              loadCounts();
              fetch(`${BASE}/registration/${selected?.submission_id}`)
                .then(r=>r.json())
                .then(d=>{ if(d.submission) setSelected(d.submission); })
                .catch(()=>{});
            }}
          />
        </>
      )}
    </div>
  );
}
